<script setup>
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import Button from '../../../../../components-next/button/Button.vue';
import CustomSwitch from './CustomSwitch.vue';
import CreateAttributeModal from './CreateAttributeModal.vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  availableAttributes: {
    type: Array,
    default: () => [],
  },
  nameLabel: {
    type: String,
    default: 'Atributo',
  },
  requiredLabel: {
    type: String,
    default: 'Obrigatório',
  }
});

const emit = defineEmits(['update', 'validation']);

const localItems = ref([...props.items]);
// Array para controlar estado dos dropdowns
const dropdownStates = ref([]);
// Estado do modal de criação
const showCreateModal = ref(false);
// Índice do campo que está criando atributo
const creatingForIndex = ref(null);

// Função para gerar a key baseada no nome
const generateKey = (name) => {
  if (!name) return '';
  // Converte para minúsculas e substitui espaços por underscores
  return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
};

// Computed para verificar se todos os itens são válidos
const areAllItemsValid = computed(() => {
  // Se não houver itens, é válido (campos são opcionais)
  if (localItems.value.length === 0) return true;
  
  // Se houver itens, todos devem ter nome preenchido
  return localItems.value.every(item => {
    return item.name && item.name.trim();
  });
});

// Emite o estado de validação sempre que muda
watch(areAllItemsValid, (newValue) => {
  emit('validation', newValue);
});

// Emite o estado inicial na montagem
onMounted(() => {
  // Atualiza a key baseada no nome, se já existir um nome
  localItems.value.forEach((item) => {
    if (item.name && !item.key) {
      item.key = generateKey(item.name);
    }
  });
  
  // Inicializa estados dos dropdowns
  dropdownStates.value = new Array(localItems.value.length).fill(false);
  
  // Atualiza o estado de validação
  emit('validation', areAllItemsValid.value);
});

// Computed para obter atributos disponíveis (não selecionados)
const getAvailableAttributesForField = (index) => {
  // Verificar se availableAttributes existe e é um array
  if (!props.availableAttributes || !Array.isArray(props.availableAttributes)) {
    return [];
  }
  
  const selectedKeys = localItems.value
    .filter((_, i) => i !== index)
    .map(item => item.key)
    .filter(Boolean);
  
  return props.availableAttributes.filter(attr => 
    !selectedKeys.includes(attr.attributeKey)
  );
};

// Função para lidar com seleção de atributo
const handleAttributeSelect = (index, attribute) => {
  if (attribute === 'custom') {
    // Abrir modal para criar novo atributo
    creatingForIndex.value = index;
    showCreateModal.value = true;
    dropdownStates.value[index] = false;
  } else {
    // Preencher com dados do atributo selecionado
    localItems.value[index].name = attribute.attributeDisplayName;
    localItems.value[index].description = attribute.attributeDescription || '';
    localItems.value[index].key = attribute.attributeKey;
    
    // Fechar dropdown
    dropdownStates.value[index] = false;
    
    // Atualizar
    updateItems();
  }
};


// Função para lidar com criação de novo atributo
const handleAttributeCreated = (newAttribute) => {
  //console.log('Evento created recebido:', newAttribute); // Debug
  
  if (creatingForIndex.value !== null) {
    const index = creatingForIndex.value;
    
    //console.log('Preenchendo campo no índice:', index); // Debug
    
    // Preencher o campo com o novo atributo criado
    localItems.value[index].name = newAttribute.attributeDisplayName;
    localItems.value[index].description = newAttribute.attributeDescription || '';
    localItems.value[index].key = newAttribute.attributeKey;
    
    // Atualizar
    updateItems();
    
    // Limpar índice
    creatingForIndex.value = null;
  }
};

// Função para fechar modal
const handleModalClose = () => {
  showCreateModal.value = false;
  creatingForIndex.value = null;
};

// Função para alternar dropdown
const toggleDropdown = (index) => {
  // Fechar todos os outros dropdowns
  dropdownStates.value.forEach((_, i) => {
    if (i !== index) {
      dropdownStates.value[i] = false;
    }
  });
  
  // Toggle do dropdown atual
  dropdownStates.value[index] = !dropdownStates.value[index];
};

const closeDropdown = (index) => {
  dropdownStates.value[index] = false;
};

const removeItem = async (index) => {
  localItems.value.splice(index, 1);
  updateItems();
  
  // Aguarda o próximo ciclo de renderização antes de validar
  await nextTick();
  
  // Força a revalidação após remoção
  emit('validation', areAllItemsValid.value);
};

const toggleRequired = (index, newValue) => {
  // Garante que estamos lidando com um booleano
  localItems.value[index].required = Boolean(newValue);
  updateItems();
};

const updateItems = () => {
  emit('update', [...localItems.value]);
};

// Watch para o nome de cada item, para gerar a key automaticamente
watch(localItems, (newItems) => {
  newItems.forEach((item) => {
    if (item.name && (!item.key || item.key !== generateKey(item.name))) {
      item.key = generateKey(item.name);
    }
  });
}, { deep: true });

// Observe os itens para sincronizar com as props
watch(() => props.items, (newItems) => {
  if (JSON.stringify(newItems) !== JSON.stringify(localItems.value)) {
    localItems.value = [...newItems];
    
    // Atualiza as keys
    localItems.value.forEach((item) => {
      // Atualiza a key se necessário
      if (item.name && (!item.key || item.key !== generateKey(item.name))) {
        item.key = generateKey(item.name);
      }
    });
    
    // Reset dropdown states
    dropdownStates.value = new Array(newItems.length).fill(false);
    
    // Emite o estado de validação ao receber novos itens
    emit('validation', areAllItemsValid.value);
  }
}, { deep: true });
</script>

<template>
  <div class="space-y-4">
    <TransitionGroup name="list-fade" tag="div">
      <div 
        v-for="(item, index) in localItems" 
        :key="`dynamic-user-field-${index}`" 
        class="relative bg-n-slate-2 dark:bg-n-slate-12/5 p-4 rounded-lg border border-n-slate-5 dark:border-n-slate-10/20 mt-7 user-field-item dynamic-field-container"
        :class="{ 'required-field is-required': item.required }"
      >
        <!-- Cabeçalho -->
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ localItems.length > 1 ? `Atributo ${localItems.length - index}` : 'Atributo' }}
          </h4>
          <Button
            icon="i-lucide-trash-2"
            variant="faded"
            color="ruby"
            size="xs"
            class="remove-button-rotate"
            @click="removeItem(index)"
            v-tooltip.top="'Remover campo'"
          />
        </div>
        
        <!-- Grid de campos -->
        <div class="space-y-3">
          <!-- Seleção de Campo Existente ou Novo -->
          <div>
            <label class="text-xs text-n-slate-11 mb-1 block">{{ nameLabel }} *</label>
            <div class="relative w-full custom-dropdown" v-on-clickaway="() => closeDropdown(index)">
              <!-- Botão do dropdown (sempre visível como no de tags) -->
              <button
                @click="toggleDropdown(index)"
                type="button"
                :class="[
                  'flex items-center justify-between px-3 h-[38px] text-sm border rounded-lg cursor-pointer w-full',
                  'bg-n-slate-1 border-n-slate-6 text-n-slate-12 hover:border-primary-600 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 focus:outline-none',
                  'dark:bg-n-slate-1 dark:border-n-slate-6 dark:text-n-slate-12 dark:hover:border-primary-600 dark:focus:border-primary-600 dark:focus:ring-primary-600/20'
                ]"
              >
                <span :class="{ 'text-n-slate-9': !item.name }">
                  {{ item.name || 'Selecione ou crie um atributo' }}
                </span>
                <span 
                  class="i-lucide-chevron-down ml-2 h-4 w-4 text-n-slate-10 transition-transform"
                  :class="{ 'rotate-180': dropdownStates[index] }"
                ></span>
              </button>
              
              <!-- Lista de opções -->
              <div 
                v-if="dropdownStates[index]"
                class="absolute top-full mt-1 left-0 w-full bg-n-slate-1 border border-n-slate-6 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                style="z-index: 1000;"
              >
                <!-- Opção para criar novo campo no sistema -->
                <button
                  @click="handleAttributeSelect(index, 'custom')"
                  type="button"
                  class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-3 first:rounded-t-lg flex items-center gap-2 border-b border-n-slate-5"
                >
                  <span class="i-lucide-database w-4 h-4 text-primary-600"></span>
                  <span class="font-medium text-primary-600">Criar novo atributo</span>
                </button>
                
                <!-- Atributos existentes -->
                <template v-if="getAvailableAttributesForField(index).length > 0">
                  <button
                    v-for="attr in getAvailableAttributesForField(index)"
                    :key="attr.id"
                    @click="handleAttributeSelect(index, attr)"
                    type="button"
                    class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-3 last:rounded-b-lg flex flex-col items-start gap-1"
                    :class="{ 'bg-n-slate-3': item.key === attr.attributeKey }"
                  >
                    <span class="font-medium">{{ attr.attributeDisplayName }}</span>
                  </button>
                </template>
                
                <div v-else class="px-3 py-2 text-sm text-n-slate-9 text-center">
                  Nenhum atributo disponível
                </div>
              </div>
            </div>
          </div>
          
          <!-- Descrição do Atributo (somente leitura) -->
          <div v-if="item.name">
            <label class="text-xs text-n-slate-11 mb-1 flex items-center gap-1">
              Descrição do Atributo
              <span 
                class="i-lucide-info w-3 h-3 text-n-slate-9"
                v-tooltip.top="`A descrição do atributo vem das configurações do sistema. Para editar, acesse Configurações → Atributos personalizados.`"
              ></span>
            </label>
            <div class="min-h-[2.5rem] px-3 py-2 text-sm bg-n-slate-1 border border-n-slate-5 rounded-lg text-n-slate-11">
              <span v-if="item.description" class="whitespace-pre-wrap">{{ item.description }}</span>
              <span v-else class="italic text-n-slate-9">Descrição não foi definida para este atributo.</span>
            </div>
          </div>
          
          <!-- Switch de Obrigatório -->
          <div class="flex items-center justify-start gap-3 pt-1">
            <label class="text-xs text-n-slate-11">
              {{ requiredLabel }}
            </label>
            <CustomSwitch 
              :model-value="item.required"
              @update:model-value="value => toggleRequired(index, value)"
            />
          </div>
        </div>
      </div>
    </TransitionGroup>
    
    <!-- Modal de criação de atributo -->
    <CreateAttributeModal
      :show="showCreateModal"
      @close="handleModalClose"
      @created="handleAttributeCreated"
    />
  </div>
</template>

<style lang="scss" scoped>
// Importar animações compartilhadas
@import '../shared/DynamicFieldAnimations.scss';

// Estilos específicos para campos de usuário
.user-field-item {
  transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
  position: relative;
  z-index: 1;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    z-index: 100; // Z-index alto para o container em hover
    
    // Destaca o switch quando hover no card
    .custom-switch {
      transform: scale(1.05);
    }
  }
  
  // Quando o dropdown está aberto, aumenta ainda mais o z-index
  &:has(.custom-dropdown div[style*="z-index: 1000"]) {
    z-index: 200 !important;
  }
}

// Garantir que o dropdown sempre fique acima
.custom-dropdown {
  position: relative;
  
  > div[style*="z-index: 1000"] {
    z-index: 1000 !important;
    position: absolute !important;
  }
}

// Usa a animação do arquivo compartilhado
// .remove-button-rotate está definido em DynamicFieldAnimations.scss

// Destaque para campos obrigatórios
.required-field {
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: -4px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: var(--primary-600);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &.is-required::after {
    opacity: 1;
  }
}
</style>